import React, { useContext, useEffect, useState } from 'react';
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Col, Form, Row, Select } from 'antd';

import {
  SalesOrderLine,
  SalesOrderLineService,
} from '@totem/components/salesOrders/list/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ExtendedObjectReference } from '@totem/types/common';
import {
  CONTRACTS_ENDPOINT,
  SALES_ORDERS_ENDPOINT,
} from '@totem/utilities/endpoints';

const FormItem = Form.Item;
const { Option } = Select;

const styles = {
  addIcon: { marginLeft: '15px', color: '#1890ff' },
  removeIcon: { color: '#1890ff' },
  formItem: { paddingBottom: '0', marginBottom: '1rem' },
};

export interface Props {
  salesOrderLine: SalesOrderLine;
  onChanged: () => void;
}

const SalesOrderLineServicesEditPanel = ({
  salesOrderLine,
  onChanged,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [availableServices, setAvailableServices] = useState<
    ExtendedObjectReference[]
  >([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');

  useEffect(() => {
    fetch(`${CONTRACTS_ENDPOINT}/services`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => res.json())
      .then((result: ExtendedObjectReference[]) => {
        setAvailableServices(result ?? []);
      });
  }, [getApiToken]);

  const assignedIds = new Set(
    (salesOrderLine.services ?? []).map((svc) => svc.id),
  );

  const handleAdd = () => {
    if (selectedServiceId === '') {
      return;
    }
    const found = availableServices.find((svc) => svc.id === selectedServiceId);
    if (!found) {
      return;
    }
    fetch(`${SALES_ORDERS_ENDPOINT}/${salesOrderLine.id}/services`, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ id: found.id, name: found.displayName }),
    }).then(() => {
      setSelectedServiceId('');

      onChanged();
    });
  };

  const handleRemove = (service: SalesOrderLineService) => {
    fetch(
      `${SALES_ORDERS_ENDPOINT}/${salesOrderLine.id}/services/${service.id}`,
      {
        method: 'DELETE',
        headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      },
    ).then(() => onChanged());
  };

  return (
    <Row>
      <Col span={12}>
        <b>Assigned Services:</b>
        <br />
        {(salesOrderLine.services ?? []).map((svc) => (
          <span key={svc.id}>
            <CloseCircleOutlined
              title="Remove Service"
              onClick={() => handleRemove(svc)}
              style={styles.removeIcon}
            />
            &nbsp;{svc.name}
            <br />
          </span>
        ))}
      </Col>
      <Col span={12}>
        <FormItem
          label="Available Services"
          colon={false}
          style={styles.formItem}
        >
          <Row>
            <Col span={20}>
              <Select value={selectedServiceId} onChange={setSelectedServiceId}>
                <Option value="">-- Select --</Option>
                {availableServices
                  .filter((svc) => !assignedIds.has(svc.id))
                  .map((svc) => (
                    <Option key={svc.id} value={svc.id}>
                      {svc.displayName}
                    </Option>
                  ))}
              </Select>
            </Col>
            <Col span={4}>
              <PlusCircleOutlined
                title="Add Service"
                onClick={handleAdd}
                style={styles.addIcon}
              />
            </Col>
          </Row>
        </FormItem>
      </Col>
    </Row>
  );
};

export default SalesOrderLineServicesEditPanel;
